import React, { useState } from "react";
import axios from "axios";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { TOOLS_MODULE } from "@/api_framework/api_config";

interface CallAnalysisResponse {
  call_summary: {
    overview: string;
    key_topics: string[];
    resolution_status: string;
  };
  sentiment_analysis: {
    overall_sentiment: string;
    sentiment_shifts: string;
    emotional_tone: string;
  };
  key_insights: {
    what_went_well: string[];
    areas_for_improvement: string[];
    training_opportunities: string[];
    process_improvements: string[];
  };
  transcript?: {
    speaker: string;
    text: string;
    start: number;
    end: number;
  }[];
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const CallAnalysisPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CallAnalysisResponse | null>(null);
  const configuration = useConfiguration();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file first.");
      return;
    }

    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);

    const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);

    try {
      const response = await axios.post(
        TOOLS_MODULE.CALL_ANALYSIS.baseURL,
        formData,
        {
          headers: {
            "Authorization": `${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setResult(response.data);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Error analyzing call:", error);
      toast.error("Failed to analyze call. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const highlightMetrics = result
    ? [
        {
          label: "Overall Sentiment",
          value: result.sentiment_analysis.overall_sentiment,
          chip: "Tone",
          accent: "from-emerald-500/80 to-green-400/80",
          detail: result.sentiment_analysis.sentiment_shifts,
        },
        {
          label: "Resolution",
          value: result.call_summary.resolution_status.replace(/_/g, " "),
          chip: "Status",
          accent: "from-blue-500/80 to-indigo-500/80",
          detail: `${result.call_summary.key_topics.length} key topics discussed`,
        },
        {
          label: "Growth Areas",
          value: `${result.key_insights.areas_for_improvement.length + result.key_insights.process_improvements.length}`,
          chip: "Actions",
          accent: "from-amber-500/80 to-orange-500/80",
          detail: "Opportunities identified for follow-up",
        },
        {
          label: "Training",
          value: `${result.key_insights.training_opportunities.length}`,
          chip: "Coaching",
          accent: "from-purple-500/80 to-pink-500/80",
          detail: result.key_insights.training_opportunities[0] || "Great spot for playbook updates",
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 font-geist text-gray-800 relative overflow-hidden">
      <div className="absolute -top-32 right-6 w-72 h-72 bg-indigo-200/60 blur-3xl rounded-full" />
      <div className="absolute -bottom-40 left-0 w-80 h-80 bg-blue-200/60 blur-3xl rounded-full" />

      <div className="relative max-w-7xl mx-auto px-4 py-10 space-y-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-4 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
              <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
              Powered by AI, caffeine, and a request from Pavitra
            </div>
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600">
              Pawlytics
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto md:mx-0">
              Upload a recording to instantly surface sentiment, resolutions, and the next best coaching steps for your team.
            </p>
          </div>

          <div className="w-full md:w-[360px] bg-white/80 backdrop-blur border border-indigo-100 shadow-xl rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Workflow</p>
              <span className="text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded-full">~1 minute</span>
            </div>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-semibold">1</div>
                <div>
                  <p className="font-semibold text-gray-800">Upload recording</p>
                  <p className="text-xs text-gray-500">MP3, MP4, MPEG, MPGA, M4A, WAV, AAC and WEBM</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold">2</div>
                <div>
                  <p className="font-semibold text-gray-800">AI analyzes</p>
                  <p className="text-xs text-gray-500">Topics, tone, resolution</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-semibold">3</div>
                <div>
                  <p className="font-semibold text-gray-800">Share insights</p>
                  <p className="text-xs text-gray-500">Coach teams faster</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[minmax(0,_2fr)_360px] md:items-stretch">
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-gray-100 p-6 space-y-4 h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">Audio recording</p>
                <p className="text-xs text-gray-500">Clearer audio leads to sharper insights.</p>
              </div>
              {loading && (
                <span className="px-3 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 font-semibold">Analyzing...</span>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <div className="relative border-2 border-dashed border-gray-300 rounded-2xl p-8 bg-gradient-to-br from-gray-50 to-white hover:border-indigo-400 hover:shadow-indigo-100 transition-all duration-300 flex flex-col items-center justify-center cursor-pointer group">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="text-center space-y-3 pointer-events-none">
                    <div className="mx-auto h-14 w-14 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-gray-800">{file ? "File ready" : "Drag & drop or click to upload"}</p>
                      <p className="text-xs text-gray-500">Supports MP3, WAV, M4A • up to 90 mins</p>
                    </div>
                  </div>
                </div>
              </label>

              {file && (
                <div className="flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-sm text-gray-700">
                  <div className="space-y-1">
                    <p className="font-semibold truncate max-w-[260px]">{file.name}</p>
                    <p className="text-xs text-gray-500">{file.type || "Audio file"} • {formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-xs text-indigo-700 font-semibold hover:text-indigo-900"
                  >
                    Clear
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !file}
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold shadow-lg transition-all duration-300 flex items-center justify-center space-x-2
                ${loading || !file ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-indigo-600 to-blue-600 hover:shadow-indigo-500/30 hover:scale-[1.02] active:scale-[0.98]"}`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Analyze Call</span>
                )}
              </button>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{file ? formatFileSize(file.size) : ""}</span>
              </div>
            </form>
          </div>

          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-gray-100 p-6 space-y-4 h-full flex flex-col">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Pro tips</p>
                <p className="text-xs text-gray-500">Set context to guide the model.</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-2">
                <span className="mt-1 w-2 h-2 rounded-full bg-indigo-400"></span>
                Mention the goal of the call at the start for clearer summaries.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 w-2 h-2 rounded-full bg-blue-400"></span>
                Keep background noise low and maintain consistent volume for sentiment tracking.
              </li>
              <li className="flex gap-2">
                <span className="mt-1 w-2 h-2 rounded-full bg-amber-400"></span>
                Flag objections or follow-ups explicitly so they surface in insights.
              </li>
            </ul>
          </div>
        </div>

        <AnimatePresence>
          {result && (
            <motion.div
              initial="hidden"
              animate="visible"
              className="space-y-8"
            >
              <motion.div variants={sectionVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {highlightMetrics.map((metric, index) => (
                  <div key={index} className="bg-white/90 backdrop-blur border border-gray-100 rounded-2xl shadow-lg p-4 space-y-2 relative overflow-hidden">
                    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${metric.accent}`} />
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase">{metric.label}</span>
                      <span className="px-2 py-1 text-[11px] rounded-full bg-gray-100 text-gray-700">{metric.chip}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 capitalize">{metric.value}</div>
                    <p className="text-xs text-gray-500 leading-snug">{metric.detail}</p>
                  </div>
                ))}
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div variants={sectionVariants} className="bg-white/90 backdrop-blur p-6 rounded-2xl shadow-lg border border-gray-100 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <span className="w-2 h-8 bg-blue-500 rounded-full"></span>
                      Call Summary
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize border
                      ${result.call_summary.resolution_status === "resolved" ? "bg-green-50 text-green-700 border-green-100" : "bg-amber-50 text-amber-700 border-amber-100"}`}>
                      {result.call_summary.resolution_status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">{result.call_summary.overview}</p>
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase">Key topics</p>
                    <div className="flex flex-wrap gap-2">
                      {result.call_summary.key_topics.map((topic, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-100">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={sectionVariants} className="bg-white/90 backdrop-blur p-6 rounded-2xl shadow-lg border border-gray-100 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-2 h-8 bg-purple-500 rounded-full"></span>
                    Sentiment Analysis
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white">
                      <span className="text-xs text-gray-500 block mb-1">Overall sentiment</span>
                      <span className="text-lg font-semibold text-gray-900 capitalize">{result.sentiment_analysis.overall_sentiment}</span>
                    </div>
                    <div className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/60">
                      <span className="text-xs text-gray-500 block mb-1">Emotional tone</span>
                      <p className="text-sm text-gray-700 italic">"{result.sentiment_analysis.emotional_tone}"</p>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="text-xs font-semibold text-gray-700 block mb-1">Sentiment shifts</span>
                    <p className="text-gray-600 text-sm leading-relaxed">{result.sentiment_analysis.sentiment_shifts}</p>
                  </div>
                </motion.div>
              </div>

              <motion.div variants={sectionVariants} className="bg-white/90 backdrop-blur p-8 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Key Insights</h3>
                  <span className="text-sm text-gray-500">{result.key_insights.what_went_well.length + result.key_insights.areas_for_improvement.length + result.key_insights.training_opportunities.length + result.key_insights.process_improvements.length} highlights</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="rounded-xl border border-green-100 bg-green-50/60 p-4">
                    <h4 className="flex items-center text-lg font-semibold text-green-700 mb-3">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      What Went Well
                    </h4>
                    <ul className="space-y-2">
                      {result.key_insights.what_went_well.map((item, i) => (
                        <li key={i} className="flex items-start text-gray-700 text-sm">
                          <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-red-100 bg-red-50/60 p-4">
                    <h4 className="flex items-center text-lg font-semibold text-red-600 mb-3">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                      Areas for Improvement
                    </h4>
                    <ul className="space-y-2">
                      {result.key_insights.areas_for_improvement.map((item, i) => (
                        <li key={i} className="flex items-start text-gray-700 text-sm">
                          <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
                    <h4 className="flex items-center text-lg font-semibold text-indigo-600 mb-3">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
                      Training Opportunities
                    </h4>
                    <ul className="space-y-2">
                      {result.key_insights.training_opportunities.map((item, i) => (
                        <li key={i} className="flex items-start text-gray-700 text-sm">
                          <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-indigo-500 rounded-full flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
                    <h4 className="flex items-center text-lg font-semibold text-amber-600 mb-3">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                      Process Improvements
                    </h4>
                    <ul className="space-y-2">
                      {result.key_insights.process_improvements.map((item, i) => (
                        <li key={i} className="flex items-start text-gray-700 text-sm">
                          <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>

              {result.transcript && result.transcript.length > 0 && (
                <motion.div variants={sectionVariants} className="bg-white/90 backdrop-blur p-8 rounded-2xl shadow-lg border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <span className="w-2 h-8 bg-teal-500 rounded-full"></span>
                      Call Transcript
                    </h3>
                    <span className="text-sm text-gray-500">{result.transcript.length} utterances</span>
                  </div>
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-4 custom-scrollbar">
                    {result.transcript.map((step, i) => (
                      <div key={i} className={`flex gap-4 ${step.speaker === "A" ? "flex-row" : "flex-row-reverse"}`}>
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
                          ${step.speaker === "A" ? "bg-indigo-100 text-indigo-700" : "bg-teal-100 text-teal-700"}`}>
                          {step.speaker}
                        </div>
                        <div className={`flex flex-col max-w-[80%] ${step.speaker === "A" ? "items-start" : "items-end"}`}>
                          <div className={`px-4 py-2 rounded-2xl text-sm leading-relaxed
                            ${step.speaker === "A" ? "bg-gray-50 text-gray-800 rounded-tl-none border border-gray-100" : "bg-indigo-600 text-white rounded-tr-none shadow-md"}`}>
                            {step.text}
                          </div>
                          <span className="text-[10px] text-gray-400 mt-1 px-1">
                            {Math.floor(step.start / 60)}:{(step.start % 60).toFixed(0).padStart(2, '0')} - {Math.floor(step.end / 60)}:{(step.end % 60).toFixed(0).padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CallAnalysisPage;
